import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { BedDouble, Bath, Ruler, MapPin, PlayCircle, Building2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { CinematicVideoDialogContent } from "@/components/media/CinematicVideoDialogContent";
import { dictionary, localizePath } from "@/i18n/translations";
import { api, resolveImageUrl } from "@/lib/api";
import { buildOpenStreetMapEmbedUrl, resolvePropertyMapCoordinates } from "@/lib/propertyMapUtils";
import { setDocumentMeta } from "@/lib/seo";

const LAND_PROPERTY_TYPES = new Set(["land", "residential-land", "commercial-land", "agricultural-land"]);
const RESIDENTIAL_PROPERTY_TYPES = new Set([
  "villa",
  "apartment",
  "palace",
  "rest-house",
  "floor-in-villa",
  "floor-in-building",
]);
const PROPERTY_AGE_TYPES = new Set([
  "villa",
  "apartment",
  "building",
  "palace",
  "rest-house",
  "floor-in-villa",
  "floor-in-building",
  "shop",
]);
const FLOORS_COUNT_PROPERTY_TYPES = new Set(["villa", "building", "palace", "shop"]);
const isRecord = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);

const hasRenderableValue = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === "number") {
    return value > 0;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (typeof value === "boolean") {
    return true;
  }

  return true;
};

export default function PropertyDetailsPage({ lang }) {
  const { propertyId } = useParams();
  const copy = dictionary[lang];

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);

  useEffect(() => {
    const fetchProperty = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/properties/${propertyId}?lang=${lang}`);
        setProperty(isRecord(response.data) ? response.data : null);
      } catch {
        setProperty(null);
        toast.error(copy.common.requestFailed);
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [copy.common.requestFailed, lang, propertyId]);

  useEffect(() => {
    const title = property
      ? `${property.title} | ${copy.seo.propertyTitlePrefix}`
      : copy.seo.homeTitle;
    const description = property ? property.description : copy.seo.homeDescription;

    setDocumentMeta({ lang, title, description });
  }, [copy.seo.homeDescription, copy.seo.homeTitle, copy.seo.propertyTitlePrefix, lang, property]);

  const galleryImages = useMemo(
    () => (property?.images || []).filter((source) => typeof source === "string" && source.trim().length > 0),
    [property],
  );

  const selectedImage = useMemo(
    () => galleryImages[activeImage] || galleryImages[0] || null,
    [activeImage, galleryImages],
  );

  const hasVideo = Boolean(property?.video_url);
  const videoPosterSource = property?.video_thumbnail_url || galleryImages[0] || "";
  const mapCoordinates = useMemo(() => resolvePropertyMapCoordinates(property), [property]);
  const mapFrameUrl = useMemo(
    () => buildOpenStreetMapEmbedUrl(mapCoordinates.lat, mapCoordinates.lng, mapCoordinates.isFallback ? 9 : 13),
    [mapCoordinates],
  );

  const specCards = useMemo(() => {
    if (!property) {
      return [];
    }

    const cards = [];
    const isLandType = LAND_PROPERTY_TYPES.has(property.property_type);
    const isBuildingType = property.property_type === "building";
    const isShopType = property.property_type === "shop";
    const isResidentialType = RESIDENTIAL_PROPERTY_TYPES.has(property.property_type);
    const propertyTypeLabel = copy.advancedSearch?.propertyTypes?.[property.property_type] || property.property_type;
    const frontageLabel =
      copy.dashboard?.streetFrontageOptions?.[property.street_frontage_type] || property.street_frontage_type;
    const unitsLabel =
      property.property_type === "shop"
        ? copy.properties.shopsCount || copy.properties.unitsCount
        : copy.properties.unitsCount;

    const pushCard = (key, label, value, icon) => {
      if (!hasRenderableValue(value)) {
        return;
      }
      cards.push({ key, label, value, icon });
    };

    if (isLandType) {
      pushCard("land-area", copy.properties.landArea || copy.properties.area, property.area_sqm, Ruler);
      pushCard("land-type", copy.properties.landType, propertyTypeLabel, Building2);
      pushCard("land-zoning", copy.properties.zoning, property.zoning, Building2);
      pushCard("land-frontage", copy.properties.frontage, frontageLabel, MapPin);
      return cards;
    }

    if (isBuildingType || isShopType) {
      pushCard("area", copy.properties.area, property.area_sqm, Ruler);
      pushCard("floors", copy.properties.floorsCount, property.floors_count, Building2);
      pushCard("units", unitsLabel, property.units_count, Building2);
      if (isShopType) {
        pushCard("frontage", copy.properties.frontage, frontageLabel, MapPin);
      }

      if (isBuildingType && property.has_elevator !== null && property.has_elevator !== undefined) {
        pushCard(
          "elevator",
          copy.properties.elevatorLabel,
          property.has_elevator ? copy.properties.elevatorYes : copy.properties.elevatorNo,
          Building2,
        );
      }

      if (PROPERTY_AGE_TYPES.has(property.property_type)) {
        const propertyAgeValue = hasRenderableValue(property.property_age)
          ? `${property.property_age} ${copy.properties.yearsSuffix}`
          : null;
        pushCard(
          "property-age",
          copy.properties.propertyAge,
          propertyAgeValue,
          Building2,
        );
      }

      return cards;
    }

    if (isResidentialType) {
      pushCard("bedrooms", copy.properties.bedrooms, property.bedrooms, BedDouble);
      pushCard("bathrooms", copy.properties.bathrooms, property.bathrooms, Bath);
      pushCard("living-rooms", copy.properties.livingRooms, property.living_rooms, Building2);
      pushCard("parking", copy.properties.parking, property.parking_spots, Building2);
      pushCard("area", copy.properties.area, property.area_sqm, Ruler);
      if (FLOORS_COUNT_PROPERTY_TYPES.has(property.property_type)) {
        pushCard("floors", copy.properties.floorsCount, property.floors_count, Building2);
      }

      if (property.property_type === "villa" && property.has_elevator !== null && property.has_elevator !== undefined) {
        pushCard(
          "elevator",
          copy.properties.elevatorLabel,
          property.has_elevator ? copy.properties.elevatorYes : copy.properties.elevatorNo,
          Building2,
        );
      }

      if (PROPERTY_AGE_TYPES.has(property.property_type)) {
        const propertyAgeValue = hasRenderableValue(property.property_age)
          ? `${property.property_age} ${copy.properties.yearsSuffix}`
          : null;
        pushCard(
          "property-age",
          copy.properties.propertyAge,
          propertyAgeValue,
          Building2,
        );
      }
    }

    return cards;
  }, [copy.advancedSearch?.propertyTypes, copy.dashboard?.streetFrontageOptions, copy.properties, property]);

  if (loading) {
    return (
      <div className="min-h-screen bg-luxury-surface px-4 py-40 text-center text-stone-600" data-testid="property-details-loading-state">
        {copy.common.loading}
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-luxury-surface px-4 py-40 text-center" data-testid="property-details-not-found-state">
        <p className="text-lg text-stone-700" data-testid="property-details-not-found-message">{copy.properties.notFound}</p>
        <Link to={localizePath(lang)} data-testid="property-details-back-link">
          <Button className="mt-6 rounded-full" data-testid="property-details-back-button">{copy.properties.back}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-luxury-surface" data-testid="property-details-root">
      <main className="mx-auto max-w-7xl space-y-10 px-4 pb-20 pt-32 sm:px-10" data-testid="property-details-main-content">
        <div className="flex flex-wrap items-center justify-between gap-4" data-testid="property-details-header">
          <div>
            <h1 className="text-3xl font-semibold text-stone-900 sm:text-5xl" data-testid="property-details-title">{property.title}</h1>
            <p className="mt-2 flex items-center gap-2 text-stone-600" data-testid="property-details-location">
              <MapPin size={17} />
              {property.location}
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3" data-testid="property-details-badges">
            <Badge className="bg-gold-600 text-stone-900" data-testid="property-details-listing-badge">
              {copy.properties[property.listing_type]}
            </Badge>
            <Badge variant="outline" data-testid="property-details-status-badge">
              {copy.properties[property.availability]}
            </Badge>

            {hasVideo && (
              <Dialog open={videoDialogOpen} onOpenChange={setVideoDialogOpen}>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full border-gold-500/40 bg-black/[0.03] text-stone-800 hover:bg-gold-50"
                  onClick={() => setVideoDialogOpen(true)}
                  data-testid="property-details-watch-video-button"
                >
                  <PlayCircle size={15} /> {copy.properties.watchVideo}
                </Button>

                <CinematicVideoDialogContent
                  open={videoDialogOpen}
                  videoUrl={property.video_url}
                  posterUrl={videoPosterSource}
                  title={copy.properties.videoDialogTitle}
                  description={copy.properties.videoDialogDescription}
                  noVideoLabel={copy.properties.noVideo}
                  clickToPlayLabel={copy.properties.watchVideo}
                  testIdPrefix="property-details-video-dialog"
                />
              </Dialog>
            )}
          </div>
        </div>

        <section className="grid gap-5 lg:grid-cols-[1fr_160px]" data-testid="property-details-gallery-section">
          <div className="overflow-hidden rounded-3xl border border-gold-500/20 bg-white shadow-lg" data-testid="property-details-main-image-wrapper">
            {selectedImage ? (
              <img
                src={resolveImageUrl(selectedImage)}
                alt={property.title}
                className="h-full w-full object-cover"
                data-testid="property-details-main-image"
              />
            ) : (
              <div
                className="flex h-[320px] w-full items-center justify-center bg-gradient-to-br from-stone-100 to-stone-200 text-sm text-stone-500"
                data-testid="property-details-main-image-fallback"
              >
                {copy.properties.gallery}
              </div>
            )}
          </div>

          <div className="grid max-h-[500px] gap-3 overflow-y-auto" data-testid="property-details-thumbnails-grid">
            {galleryImages.map((source, index) => (
              <button
                key={source}
                className={`overflow-hidden rounded-xl border ${index === activeImage ? "border-gold-500" : "border-transparent"}`}
                onClick={() => setActiveImage(index)}
                data-testid={`property-details-thumbnail-button-${index}`}
              >
                <img
                  src={resolveImageUrl(source)}
                  alt={`${property.title}-${index}`}
                  className="h-24 w-full object-cover"
                  data-testid={`property-details-thumbnail-image-${index}`}
                />
              </button>
            ))}
          </div>
        </section>

        <section className="grid gap-8 lg:grid-cols-2" data-testid="property-details-info-section">
          <div className="space-y-5 rounded-3xl border border-gold-500/20 bg-white p-6 shadow" data-testid="property-details-content-card">
            <h2 className="text-base text-stone-500" data-testid="property-details-section-label">{copy.properties.detailsSection}</h2>
            <p className="text-base text-stone-700" data-testid="property-details-description">{property.description}</p>

            {specCards.length > 0 && (
              <div className="grid gap-3 sm:grid-cols-3" data-testid="property-details-stats-grid">
                {specCards.map((spec) => {
                  const Icon = spec.icon;
                  return (
                    <div
                      key={spec.key}
                      className="rounded-2xl bg-stone-100 p-4 text-stone-700"
                      data-testid={`property-details-spec-card-${spec.key}`}
                    >
                      <p className="text-xs text-stone-500" data-testid={`property-details-spec-label-${spec.key}`}>{spec.label}</p>
                      <p className="mt-2 flex items-center gap-2 text-lg font-semibold" data-testid={`property-details-spec-value-${spec.key}`}>
                        <Icon size={16} />
                        {spec.value}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}

            <p className="text-3xl font-semibold text-gold-700" data-testid="property-details-price">
              {property.price.toLocaleString()} {property.currency}
            </p>

            <a href={`tel:${property.contact_phone || ""}`} data-testid="property-details-contact-link">
              <Button className="w-full rounded-full bg-stone-900 py-6 text-cream-50 hover:bg-stone-700" data-testid="property-details-contact-button">
                {copy.properties.contact}
              </Button>
            </a>
          </div>

          <div className="overflow-hidden rounded-3xl border border-gold-500/20 bg-white shadow" data-testid="property-details-map-card">
            <div className="border-b border-stone-200 p-4 text-sm text-stone-700" data-testid="property-details-map-title">
              {copy.properties.locationMap}
            </div>
            <iframe
              title="property-location"
              src={mapFrameUrl}
              className="h-[420px] w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="property-details-map-frame"
            />
          </div>
        </section>
      </main>
    </div>
  );
}
